import apiClient from "../api/client";
import { useState, useEffect } from 'react';

export interface ImageTokenSettings {
  token: string;
  urlContains: string[];
}

let settingsPromise: Promise<ImageTokenSettings> | null = null;
let cachedSettings: ImageTokenSettings | null = null;

/**
 * Fetches the image token settings from the API endpoint.
 * This is cached using a promise so that multiple concurrent components calling this
 * will only trigger a single HTTP request.
 * 
 * @returns A promise resolving to the ImageTokenSettings object containing the SAS token and target domains.
 */
export function getImageTokenSettings(): Promise<ImageTokenSettings> {
  if (cachedSettings) {
    return Promise.resolve(cachedSettings);
  }
  if (!settingsPromise) {
    settingsPromise = apiClient.get<ImageTokenSettings>("/api/Menu/ImageTokenSettings")
      .then(res => {
        cachedSettings = res.data;
        return cachedSettings;
      })
      .catch(err => {
        console.error("Failed to load image token settings", err);
        return { token: "", urlContains: [] };
      });
  }
  return settingsPromise;
}

/**
 * Utility to append a SAS token to a given imageUrl if the URL matches any of the domains
 * configured in the token settings.
 * 
 * @param imageUrl The raw image URL from the database.
 * @param settings The fetched image token settings (token and domain/base URL filters).
 * @returns The tokenized image URL if there's a match, or the original raw URL.
 */
export function tokenizeImageUrl(imageUrl: string | undefined | null, settings: ImageTokenSettings | null): string {
  if (!imageUrl) return "";
  if (!settings || !settings.token || !settings.urlContains) return imageUrl;
  
  const matches = settings.urlContains.some(domain => imageUrl.includes(domain));
  if (matches) {
    return imageUrl + settings.token;
  }
  return imageUrl;
}

/**
 * Custom React hook that resolves and returns the tokenized URL of an image.
 * 
 * Performance: When `enabled` is false, it short-circuits and immediately returns
 * the original imageUrl without querying the backend API.
 * 
 * @param imageUrl The raw image URL or filename from the database.
 * @param enabled Whether to enable dynamic tokenization for this component/session (defaults to false).
 * @returns The final URL to use for rendering the image (either tokenized or raw).
 */
export function useImageTokenUrl(imageUrl: string | undefined | null, enabled: boolean = false): string {
  const [tokenizedUrl, setTokenizedUrl] = useState<string>('');

  useEffect(() => {
    if (!imageUrl || !enabled) {
      setTokenizedUrl('');
      return;
    }
    getImageTokenSettings().then(settings => {
      setTokenizedUrl(tokenizeImageUrl(imageUrl, settings));
    });
  }, [imageUrl, enabled]);

  return (enabled && tokenizedUrl) ? tokenizedUrl : (imageUrl || '');
}
